import { useCallback, useState } from 'react';

import type { BuckChatConfig, OtpContext } from '../../../types';
import { ANALYTICS_KEYS, BUCK_EVENTS } from '../../../utils/analytics';
import { OtpErrorType } from '../../../utils/authApi';
import { useSocialAuth } from './useSocialAuth';
import { useTokenExpiration } from './useTokenExpiration';

export type SignupStep = 'method' | 'code' | 'profile';

export type AnalyticsHandler = (event: string, data: Record<string, unknown>) => void;

type UseSignupFlowOptions = {
  config: BuckChatConfig;
  context?: OtpContext;
  onSuccess: (context?: OtpContext) => void;
  onAbandoned?: (step: string) => void;
  onAnalyticsEvent?: AnalyticsHandler;
};

type UseSignupFlowReturn = {
  step: SignupStep;
  email: string;
  idpToken: string | null;
  apiBaseUrl: string;
  handleGoogleAuth: () => void;
  handleFacebookAuth: () => void;
  handleCodeSent: (email: string) => void;
  handleVerified: (idpToken: string, tokenExpiresAt: number, isExistingUser: boolean) => void;
  handleSessionExpired: () => void;
  handleBackToMethod: () => void;
};

export const useSignupFlow = ({
  config,
  context,
  onSuccess,
  // onAbandoned,
  onAnalyticsEvent,
}: UseSignupFlowOptions): UseSignupFlowReturn => {
  const [step, setStep] = useState<SignupStep>('method');
  const [email, setEmail] = useState('');
  const apiBaseUrl = config.apiBaseUrl ?? '';

  const handleSessionExpired = useCallback(() => {
    onAnalyticsEvent?.(BUCK_EVENTS.OTP_ERROR, {
      [ANALYTICS_KEYS.ERROR_TYPE]: OtpErrorType.TOKEN_EXPIRED,
      [ANALYTICS_KEYS.ERROR_MESSAGE]: 'Session timeout on profile step',
      [ANALYTICS_KEYS.STEP]: 'profile',
      [ANALYTICS_KEYS.LISTING_ID]: context?.listingId,
      [ANALYTICS_KEYS.PACKAGE_ID]: context?.packageId,
    });

    setStep('method');
    setEmail('');
  }, [context, onAnalyticsEvent]);

  const { idpToken, setToken, clearToken } = useTokenExpiration({
    enabled: step === 'profile',
    onExpired: handleSessionExpired,
  });

  const { handleGoogleAuth, handleFacebookAuth } = useSocialAuth({
    config,
    context,
    onAnalyticsEvent,
  });

  // Step transition callbacks
  const handleCodeSent = useCallback((sentEmail: string) => {
    setEmail(sentEmail);
    setStep('code');
  }, []);

  const handleVerified = useCallback(
    (token: string, tokenExpiresAt: number, isExistingUser: boolean) => {
      setToken(token, tokenExpiresAt);

      if (isExistingUser) {
        // Existing user - login complete
        onAnalyticsEvent?.(BUCK_EVENTS.SIGNUP_COMPLETED, {
          [ANALYTICS_KEYS.METHOD]: 'email',
          [ANALYTICS_KEYS.USER_TYPE]: 'existing',
          [ANALYTICS_KEYS.LISTING_ID]: context?.listingId,
          [ANALYTICS_KEYS.PACKAGE_ID]: context?.packageId,
        });
        onSuccess(context);
      } else {
        // New user - need profile step
        setStep('profile');
      }
    },
    [context, onAnalyticsEvent, onSuccess, setToken]
  );

  const handleBackToMethod = useCallback(() => {
    setStep('method');
    clearToken();
  }, [clearToken]);

  return {
    step,
    email,
    idpToken,
    apiBaseUrl,
    handleGoogleAuth,
    handleFacebookAuth,
    handleCodeSent,
    handleVerified,
    handleSessionExpired,
    handleBackToMethod,
  };
};
